import React from 'react';
import PropTypes from 'prop-types';

import {translate, Translate} from 'views/module';

import RenderAllFormComponent from './RenderAllFormComponent';

const NON_DIGIT_REGEX = /[^0-9]/g;

const ContactFormComponent = (props) => {
  const {
    onSubmit,
    isSubmitting,
    onEmailCaptured,
    onProgressChange,
    isFinalForm,
  } = props;

  const [pageIndex, setPageIndex] = React.useState(0);
  const [answersByQuestion, setAnswersByQuestion] = React.useState({});

  const pages = React.useMemo(() => ([
    {
      title: translate('form.IntakeForms.Contact.Pages.Name.Title'),
      prompt: translate('form.IntakeForms.Contact.Pages.Name.Prompt'),
      compactFields: ['firstName', 'lastName'],
      fieldLayout: {
        'firstName': '100%',
        'lastName': '100%',
      },
      renderAll: true,
      questions: {
        'firstName': {
          questionId: 'firstName',
          fieldType: 'text',
          rank: 1,
          questionData: {
            label: translate('form.IntakeForms.Contact.Questions.FirstName.Label'),
            placeholder: translate('form.IntakeForms.Contact.Questions.FirstName.Placeholder'),
            helpText: '',
            required: true,
          },
        },
        'lastName': {
          questionId: 'lastName',
          fieldType: 'text',
          rank: 2,
          questionData: {
            label: translate('form.IntakeForms.Contact.Questions.LastName.Label'),
            placeholder: translate('form.IntakeForms.Contact.Questions.LastName.Placeholder'),
            helpText: '',
            required: true,
          },
        },
      },
    },
    {
      title: translate('form.IntakeForms.Contact.Pages.Address.Title'),
      prompt: translate('form.IntakeForms.Contact.Pages.Address.Prompt'),
      compactFields: ['streetAddress', 'apartmentUnit', 'city', 'province', 'postalCode'],
      fieldLayout: {
        'streetAddress': '100%',
        'apartmentUnit': '100%',
        'city': '45%',
        'province': '45%',
        'postalCode': '100%',
      },
      renderAll: true,
      questions: {
        'streetAddress': {
          questionId: 'streetAddress',
          fieldType: 'text',
          rank: 1,
          questionData: {
            label: translate('form.IntakeForms.Contact.Questions.StreetAddress.Label'),
            placeholder: translate('form.IntakeForms.Contact.Questions.StreetAddress.Placeholder'),
            helpText: '',
            required: true,
          },
        },
        'apartmentUnit': {
          questionId: 'apartmentUnit',
          fieldType: 'text',
          rank: 2,
          questionData: {
            label: translate('form.IntakeForms.Contact.Questions.ApartmentUnit.Label'),
            placeholder: translate('form.IntakeForms.Contact.Questions.ApartmentUnit.Placeholder'),
            helpText: '',
            required: false,
          },
        },
        'city': {
          questionId: 'city',
          fieldType: 'text',
          rank: 3,
          questionData: {
            label: translate('form.IntakeForms.Contact.Questions.City.Label'),
            placeholder: translate('form.IntakeForms.Contact.Questions.City.Placeholder'),
            helpText: '',
            required: true,
          },
        },
        'province': {
          questionId: 'province',
          fieldType: 'dropdown',
          rank: 4,
          questionData: {
            label: translate('form.IntakeForms.Contact.Questions.Province.Label'),
            placeholder: translate('form.IntakeForms.Contact.Questions.Province.Placeholder'),
            helpText: '',
            required: true,
          },
          options: [
            {label: translate('form.IntakeForms.Contact.Questions.Province.Options.AB'), value: 'AB', isOther: false},
            {label: translate('form.IntakeForms.Contact.Questions.Province.Options.BC'), value: 'BC', isOther: false},
            {label: translate('form.IntakeForms.Contact.Questions.Province.Options.MB'), value: 'MB', isOther: false},
            {label: translate('form.IntakeForms.Contact.Questions.Province.Options.NB'), value: 'NB', isOther: false},
            {label: translate('form.IntakeForms.Contact.Questions.Province.Options.NL'), value: 'NL', isOther: false},
            {label: translate('form.IntakeForms.Contact.Questions.Province.Options.NT'), value: 'NT', isOther: false},
            {label: translate('form.IntakeForms.Contact.Questions.Province.Options.NS'), value: 'NS', isOther: false},
            {label: translate('form.IntakeForms.Contact.Questions.Province.Options.NU'), value: 'NU', isOther: false},
            {label: translate('form.IntakeForms.Contact.Questions.Province.Options.ON'), value: 'ON', isOther: false},
            {label: translate('form.IntakeForms.Contact.Questions.Province.Options.PE'), value: 'PE', isOther: false},
            {label: translate('form.IntakeForms.Contact.Questions.Province.Options.QC'), value: 'QC', isOther: false},
            {label: translate('form.IntakeForms.Contact.Questions.Province.Options.SK'), value: 'SK', isOther: false},
            {label: translate('form.IntakeForms.Contact.Questions.Province.Options.YT'), value: 'YT', isOther: false},
          ],
        },
        'postalCode': {
          questionId: 'postalCode',
          fieldType: 'text',
          rank: 5,
          questionData: {
            label: translate('form.IntakeForms.Contact.Questions.PostalCode.Label'),
            placeholder: translate('form.IntakeForms.Contact.Questions.PostalCode.Placeholder'),
            helpText: '',
            required: true,
          },
        },
      },
    },
    {
      title: translate('form.IntakeForms.Contact.Pages.ContactInfo.Title'),
      prompt: translate('form.IntakeForms.Contact.Pages.ContactInfo.Prompt'),
      compactFields: ['phoneNumber', 'email'],
      fieldLayout: {
        'phoneNumber': '100%',
        'email': '100%',
        'privacyPolicy': '100%',
      },
      renderAll: true,
      questions: {
        'phoneNumber': {
          questionId: 'phoneNumber',
          fieldType: 'phoneNumber',
          rank: 1,
          questionData: {
            label: translate('form.IntakeForms.Contact.Questions.PhoneNumber.Label'),
            placeholder: translate('form.IntakeForms.Contact.Questions.PhoneNumber.Placeholder'),
            helpText: '',
            required: true,
          },
        },
        'email': {
          questionId: 'email',
          fieldType: 'email',
          rank: 2,
          questionData: {
            label: translate('form.IntakeForms.Contact.Questions.Email.Label'),
            placeholder: translate('form.IntakeForms.Contact.Questions.Email.Placeholder'),
            helpText: '',
            required: true,
          },
        },
        'privacyPolicy': {
          questionId: 'privacyPolicy',
          fieldType: 'checkbox',
          rank: 3,
          questionData: {
            helpText: '',
            required: true,
          },
          options: [
            {
              label: <Translate expression='form.IntakeForms.Contact.Questions.PrivacyPolicy.Label' />,
              value: 'privacyPolicy',
              isOther: false,
            },
          ],
        },
      },
    },
  ]), []);


  React.useLayoutEffect(() => {
    onProgressChange?.({current: pageIndex + 1, total: pages.length});
  }, [pageIndex, pages.length, onProgressChange]);

  const currentPage = pages[pageIndex];
  const pageQuestions = React.useMemo(() => {
    const questionsById = currentPage?.questions || {};
    // sorting questions by their assigned rank
    return Object.values(questionsById).sort((a, b) => (a.rank || 0) - (b.rank || 0));
  }, [currentPage]);

  const handleTextChange = (questionId, value) => {
    const nextValue = value ?? '';
    setAnswersByQuestion((prev) => ({
      ...prev,
      [questionId]: {
        bioQuestionId: questionId,
        fieldType: 'short_text',
        questionData: {
          value: nextValue,
        },
      },
    }));
    if (questionId === 'email') {
      onEmailCaptured?.(nextValue?.toLowerCase());
    }
  };

  const handleRadioSelect = (questionId, option) => {
    setAnswersByQuestion((prev) => ({
      ...prev,
      [questionId]: {
        bioQuestionId: questionId,
        fieldType: 'short_text',
        questionData: {
          value: option.isOther ? 'other' : option.value,
        },
      },
    }));
  };

  const handleCheckboxToggle = (questionId, optionValue, checked) => {
    const isObjectQuestion = ['careSettings', 'accreditations', 'careTasks'].includes(questionId);
    setAnswersByQuestion((prev) => {
      const currentValue = prev[questionId]?.questionData?.value;
      const currentValues = currentValue?.selected || currentValue || [];
      const nextValues = checked ? [...currentValues, optionValue] : currentValues.filter((value) => value !== optionValue);
      const nextValue = isObjectQuestion ? {selected: nextValues, otherText: prev[questionId]?.questionData?.value?.otherText || ''} : nextValues;
      return {
        ...prev,
        [questionId]: {
          bioQuestionId: questionId,
          fieldType: isObjectQuestion ? 'object' : 'checkbox',
          questionData: {
            value: nextValue,
          },
        },
      };
    });
  };

  const handleOtherTextChange = (questionId, value) => {
    const isObjectQuestion = ['careSettings', 'accreditations', 'careTasks'].includes(questionId);
    setAnswersByQuestion((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        bioQuestionId: questionId,
        fieldType: isObjectQuestion ? 'object' : (prev[questionId]?.fieldType || 'checkbox'),
        questionData: {
          value: isObjectQuestion ?
            {
              selected: prev[questionId]?.questionData?.value?.selected || [],
              otherText: value ?? '',
            } :
            value ?? '',
        },
      },
    }));
  };

  const isNextDisabled = pageQuestions.some((question) => {
    const required = question?.questionData?.required;
    if (!required) return false;
    const answer = answersByQuestion[question.questionId];
    if (
      question.fieldType === 'text' ||
      question.fieldType === 'phoneNumber' ||
      question.fieldType === 'email' ||
      question.fieldType === 'dropdown'
    ) {
      const value = answer?.questionData?.value || '';
      return !Boolean(value.trim());
    }
    if (question.fieldType === 'radio') {
      const rawValue = answer?.questionData?.value;
      if (rawValue === 'other') {
        return true;
      }
      if (rawValue && !question.options?.some((opt) => opt.value === rawValue)) {
        return !Boolean(String(rawValue).trim());
      }
      return !Boolean(rawValue);
    }
    if (question.fieldType === 'checkbox') {
      const rawValue = answer?.questionData?.value;
      const values = rawValue?.selected || rawValue || [];
      const otherText = rawValue?.otherText || '';
      if (values.includes('other')) {
        return !Boolean(String(otherText).trim());
      }
      return values.length === 0;
    }
    return false;
  });

  const handleSubmit = async () => {
    if (pageIndex < pages.length - 1) {
      setPageIndex((idx) => idx + 1);
      return;
    }
    const bioQuestions = Object.values(answersByQuestion).filter((entry) => {
      const value = entry?.questionData?.value;
      if (value && typeof value === 'object') {
        const selected = value.selected || [];
        const otherText = value.otherText ?? '';
        return selected.length > 0 || Boolean(String(otherText).trim());
      }
      if (value && value.length !== undefined) {
        return value.length > 0;
      }
      if (value === 'other') {
        return false;
      }
      return value !== '';
    });
    const payload = {
      bioQuestions,
      email: (answersByQuestion.email?.questionData?.value || '').trim(),
      phoneNumber: (answersByQuestion.phoneNumber?.questionData?.value || '').replace(NON_DIGIT_REGEX, ''),
      firstName: (answersByQuestion.firstName?.questionData?.value || '').trim(),
      lastName: (answersByQuestion.lastName?.questionData?.value || '').trim(),
    };
    onSubmit(payload);
  };

  const handlePrevious = () => {
    if (pageIndex > 0) {
      setPageIndex((idx) => idx - 1);
    }
  };

  return (
    <RenderAllFormComponent
      pageTitle={currentPage?.prompt || currentPage?.title}
      pageQuestions={pageQuestions}
      answersByQuestion={answersByQuestion}
      onTextChange={handleTextChange}
      onRadioSelect={handleRadioSelect}
      onCheckboxToggle={handleCheckboxToggle}
      onOtherTextChange={handleOtherTextChange}
      onSubmit={handleSubmit}
      onPrevious={handlePrevious}
      isSubmitting={isSubmitting}
      isNextDisabled={isNextDisabled}
      hasPrevious={pageIndex > 0}
      hasNextPage={pageIndex < pages.length - 1}
      isFinalSubmit={Boolean(isFinalForm && pageIndex >= pages.length - 1)}
      fieldLayout={currentPage?.fieldLayout}
      compactFields={currentPage?.compactFields}
    />
  );
};

export const ContactForm = {
  title: translate('form.IntakeForms.Contact.Title'),
  formVersion: 1,
  description: translate('form.IntakeForms.Contact.Description'),
  component: ContactFormComponent,
};

ContactFormComponent.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  onEmailCaptured: PropTypes.func,
  onProgressChange: PropTypes.func,
  isFinalForm: PropTypes.bool,
};

export default React.memo(ContactFormComponent);
